import type {FinishedResponseInteractionService} from 'shared/services';
import type {Auth, BasicUser, User} from 'src/typings';
import {byProperty, findFirst} from 'shared/utils/array-utils';
import type TransactionDataService from './transaction.data-service';
import type PaymentDataService from '../../payment/payment.data-service';
import type {AmountType, CreditTransfer, PayRequest} from '../../payment/types';
import type {GroupTransaction, Transaction, TransactionSums} from './types';
import {CASH_PAYMENT_PROVIDER_NAME, CASH_PAYMENT_PROVIDER_TYPE, GATEWAY_PAYMENT_PROVIDER_TYPE, MANUAL_TERMINAL_PAYMENT_PROVIDER_NAME, TERMINAL_PAYMENT_PROVIDER_TYPE} from './types';
import {isNullOrUndefined} from 'shared/utils/custom-utils';
import type {UserId} from 'src/features/user/types';
import type {AmountTypeDataService} from 'src/features/payment/amount-type.data-service';

const UPDATE_FINANCE_PANEL_EVENT = 'update-finance-panel';

export class KpUserFinancePanelService {
    public static serviceName = 'kpUserFinancePanelService';
    private eventTarget = new EventTarget();

    /*@ngInject*/
    constructor(private transactionDataService: TransactionDataService,
                private amountTypeDataService: AmountTypeDataService,
                private paymentDataService: PaymentDataService,
                private currentAuth: Auth,
                private finishedResponseInteractionService: FinishedResponseInteractionService) {
    }

    public async loadAllTransactions(userId: UserId): Promise<Transaction[]> {
        try {
            return await this.transactionDataService.query(userId);
        } catch (errResponse) {
            this.finishedResponseInteractionService.showFailedResponseInToast(errResponse);
            return [];
        }
    }

    public async loadAllAmountTypes(): Promise<AmountType[]> {
        try {
            return await this.amountTypeDataService.getAll();
        } catch (errResponse) {
            this.finishedResponseInteractionService.showFailedResponseInToast(errResponse);
            return [];
        }
    }

    public calculateGroups(transactions: Transaction[]): GroupTransaction[] {
        const groups: GroupTransaction[] = [];
        groups.forEach((g) => g.sum = 0);
        transactions.forEach((a) => {
            let groupFromList = findFirst(groups, byProperty('typeId', a.typeId));
            if (isNullOrUndefined(groupFromList)) {
                groupFromList = {
                    text: a.text,
                    sum: 0,
                    type: a.type,
                    typeId: a.typeId,
                    debt() {
                        return this.sum < 0;
                    }
                };
                groups.push(groupFromList);
            }
            groupFromList.sum += a.sum;
        });

        return groups;
    }

    public calculateSums(transactions: Transaction[], groups: GroupTransaction[]): TransactionSums {
        let sum = 0;
        let debtsSum = 0;
        let creditsSum = 0;

        transactions.forEach((transaction) => sum += transaction.sum);
        groups.forEach((group) => {
            if (group.sum < 0) {
                debtsSum += group.sum;
            }
            if (group.sum > 0) {
                creditsSum += group.sum;
            }
        });

        return {sum, debtsSum, creditsSum};
    }

    public filterAllByGroup(transactions: Transaction[], group: GroupTransaction): Transaction[] {
        return transactions.filter((a) => a.typeId === group.typeId);
    }

    public async forgive(transaction: Transaction): Promise<boolean> {
        try {
            await this.transactionDataService.remove(transaction.id);
            return true;
        } catch (errResponse) {
            this.finishedResponseInteractionService.showFailedResponseInToast(errResponse);
            return false;
        }
    }

    public async payAllUserDebts(provider: string, user: User): Promise<boolean> {
        const payRequest = {
            provider,
            payer: this.getPayer(provider, user),
            cashierDepartment: null,
            itemsSpec: {
                owner: [user],
                directPaymentOwner: user
            }
        } as PayRequest;

        try {
            const response = await this.paymentDataService.pay(payRequest);
            await this.finishedResponseInteractionService.showSuccessResponseInModalWindow(response);
            return true;
        } catch (response) {
            await this.finishedResponseInteractionService.showFailedInModalWindow(response);
            return false;
        }
    }

    private getPayer(provider: string, user: BasicUser): BasicUser {
        if ([CASH_PAYMENT_PROVIDER_NAME, MANUAL_TERMINAL_PAYMENT_PROVIDER_NAME].includes(provider)) {
            return user;
        }
        return this.currentAuth.activeUser;
    }

    public canShowPayButtonOf(paymentProviderName: string, debtsSum: number, isAnyCreditableAmountPresent: boolean, creditPaymentEnabled: boolean): boolean {
        const type = this.getPaymentProviderType(paymentProviderName);
        switch (type) {
            case CASH_PAYMENT_PROVIDER_TYPE:
                return true;
            case TERMINAL_PAYMENT_PROVIDER_TYPE:
                return true;
            case GATEWAY_PAYMENT_PROVIDER_TYPE:
                if (debtsSum < 0) {
                    return true;
                }
                if (creditPaymentEnabled) {
                    return isAnyCreditableAmountPresent;
                }
                return false;

        }
        throw new Error(`unknown payment provider type ${type}`);
    }

    public getPaymentProviderType(paymentProviderName: string): string {
        if (paymentProviderName === CASH_PAYMENT_PROVIDER_NAME) {
            return CASH_PAYMENT_PROVIDER_TYPE;
        }
        if (paymentProviderName === MANUAL_TERMINAL_PAYMENT_PROVIDER_NAME) {
            return TERMINAL_PAYMENT_PROVIDER_TYPE;
        }
        if (paymentProviderName.startsWith(GATEWAY_PAYMENT_PROVIDER_TYPE)) {
            return GATEWAY_PAYMENT_PROVIDER_TYPE;
        }
        throw new Error(`unknown payment provider with name ${paymentProviderName}`);
    }

    public updateFinancePanel() {
        this.eventTarget.dispatchEvent(new CustomEvent<void>(UPDATE_FINANCE_PANEL_EVENT));
    }

    public addUpdateFinancePanelHandler(callback: () => void) {
        this.eventTarget.addEventListener(UPDATE_FINANCE_PANEL_EVENT, callback);
        return () => this.eventTarget.removeEventListener(UPDATE_FINANCE_PANEL_EVENT, callback);
    }

    public isAnyCreditableAmountPresent(amountTypes: AmountType[]): boolean {
        return amountTypes.some((amountType) => amountType?.creditable === true);
    }

    public getCreditTypeIfOnlyOneExist(amountTypes: AmountType[]): AmountType {
        const creditTypes = amountTypes.filter((amountType) => amountType?.creditable === true);

        if (creditTypes.length === 1) {
            return creditTypes[0];
        }
        return null;
    }

    public async payByCredit(group: GroupTransaction, user: User, amountTypes: AmountType[]): Promise<boolean>{
        try {
            const amountType = this.getCreditTypeIfOnlyOneExist(amountTypes);
            const request = {
                owner: user,
                targetType: group.type,
                sourceTypes: isNullOrUndefined(amountType) ? null : [amountType]
            } as CreditTransfer;
            const actionResponse = await this.paymentDataService.payByCredit(request);
            await this.finishedResponseInteractionService.showSuccessResponseInToastIfPossible(actionResponse);
            return true;

        } catch (errResponse) {
            this.finishedResponseInteractionService.showFailedResponseInToast(errResponse);
            return false;
        }
    }
}
